/* Copyright (c) 2015-present, salesforce.com, inc. All rights reserved */
/* Licensed under BSD 3-Clause - see LICENSE.txt or git.io/sfdc-license */

// # Data Table Component

// Implements the [Data Table design pattern](https://www.lightningdesignsystem.com/components/data-tables) in React.

// ## Dependencies

// ### React
import React from 'react';
import PropTypes from 'prop-types';
// ### shortid
// [npmjs.com/package/shortid](https://www.npmjs.com/package/shortid)
// shortid is a short, non-sequential, url-friendly, unique id generator
import shortid from 'shortid';
// ### classNames
import classNames from 'classnames';
// ### assign
import assign from 'lodash.assign';
// ### reject
import reject from 'lodash.reject';
// ## Children
import DataTableCell from './cell';
import DataTableColumn from './column';
import DataTableHead from './private/head';
import DataTableRow from './private/row';
import DataTableRowActions from './row-actions';
// ## Constants
import {DATA_TABLE, DATA_TABLE_CELL, DATA_TABLE_HEAD, DATA_TABLE_ROW} from '@salesforce/design-system-react/utilities/constants';
import {CustomPropTypes} from "../../propTypes/customPropTypes";
import {Log} from "../../log"

// Safely get the length of an array, returning 0 for invalid input.
const count = (array) => (Array.isArray(array) ? array.length : 0);


/**
 * DataTables support the display of structured data in rows and columns with an HTML table. To sort, filter or paginate the table, simply update the data passed in the items to the table and it will re-render itself appropriately. The table will throw a sort event as needed, and helper components for paging and filtering are coming soon.
 *
 */
function DataTable(props) {
    const {items, onRowChange} = props;



    const numRows = count(props.items);
    const numSelected = count(props.selection);
    const canSelectRows = props.selectRows && numRows > 0;
    const canShowDetails = props.detailsComponent !== undefined;
    const allSelected = canSelectRows && numRows === numSelected;
    const indeterminateSelected = canSelectRows && numRows !== numSelected && numSelected !== 0;
    const columns = [];
    let RowActions = null;

    const handleToggleAll = (e, {checked}) => {
        Log.Debug("toggle All")
        Log.Debug(checked)
         if (typeof onRowChange === 'function') {
            const selection = checked ? [...items] : [];
            onRowChange(e, {selection});
        }
    };

    const handleRowToggle = (item, selected, e) => {
        if (typeof onRowChange === 'function') {
            let selection;

            if (selected) {
                selection = [...props.selection, item];
            } else {
                selection = reject(props.selection, item);
            }

            onRowChange(e, {selection});
        }
    };






    //Log.Debug("DataTable selection: ", props.selection);

    // //check if selection contains elements not in item List and remove them
    // if (props.selection) {
    //     let notInItemList
    //     notInItemList = reject(props.selection,item => props.items.includes(item));
    //     notInItemList.map(e => props.selection.remove(e))
    // }

    React.Children.forEach(props.children, (child, ) => {
        // TODO: The displayName should not be forced
        if (child && child.type.displayName === DataTableColumn.displayName) {
            const {children, ...columnProps} = child.props;

            const newProps = assign({}, props);
            delete newProps.children;
            assign(newProps, columnProps);

            let Cell;
            if (children && children.type.displayName === DATA_TABLE_CELL) {
                Cell = children.type;
                assign(newProps, children.props);
            } else {
                Cell = DataTableCell;
            }

            columns.push({
                Cell,
                props: newProps,
                dataTableProps: newProps,
            });
        } else if (
            child &&
            child.type.displayName === DataTableRowActions.displayName
        ) {
            RowActions = child;
        }
    });

    const assistiveText = {
        ...DataTable.defaultProps.assistiveText,
        ...props.assistiveText,
    };
    if (props.assistiveTextForActionsHeader) {
        assistiveText.actionsHeader = props.assistiveTextForActionsHeader;
    }
    if (props.assistiveTextForSelectAllRows) {
        assistiveText.selectAllRows = props.assistiveTextForSelectAllRows;
    }
    if (props.assistiveTextForColumnSortedAscending) {
        assistiveText.columnSortedAscending = props.assistiveTextForColumnSortedAscending;
    }
    if (props.assistiveTextForColumnSortedDescending) {
        assistiveText.columnSortedDescending = props.assistiveTextForColumnSortedDescending;
    }
    if (props.assistiveTextForColumnSort) {
        assistiveText.columnSort = props.assistiveTextForColumnSort;
    }
    if (props.assistiveTextForSelectRow) {
        assistiveText.selectRow = props.assistiveTextForSelectRow;
    }

    let hasColumnFilter = false
    let filterCells = [<td key={0}/>]
    columns.forEach(column => {
        if(column?.props?.filterElem) {
            filterCells.push(<td key={column.props?.column.heading}>{column.props?.filterElem}</td>)
            hasColumnFilter = true
        } else {
            filterCells.push(<td key={column.props?.column?.heading}/>)
        }

    })


    return (
        <table
            className={classNames(
                'slds-table',
                {
                    'slds-table--fixed-layout': props.fixedLayout,
                    'slds-table_resizable-cols': props.fixedLayout,
                    'slds-table_bordered': !props.unborderedRow,
                    'slds-table_cell-buffer':
                        !props.fixedLayout && !props.unbufferedCell,
                    'slds-max-medium-table_stacked': props.stacked,
                    'slds-max-medium-table_stacked-horizontalviewports': props
                        .stackedHorizontal,
                    'slds-table_striped': props.striped,
                    'slds-table_col-bordered': props.columnBordered,
                    'slds-no-row-hover': props.noRowHover,
                },
                props.className
            )}
            id={props.id}
            role={props.fixedLayout ? 'grid' : null}
        >
            <DataTableHead
                assistiveText={assistiveText}
                allSelected={allSelected}
                indeterminateSelected={indeterminateSelected}
                canSelectRows={canSelectRows}
                columns={columns}
                id={`${props.id}-${DATA_TABLE_HEAD}`}
                onToggleAll={handleToggleAll}
                onSort={props.onSort}
                showRowActions={!!RowActions}
                canShowDetails={canShowDetails}
            />
            <tbody>
            {
                hasColumnFilter ?
                filterCells : null
            }
            {numRows > 0
                ? props.items.map((item, i) => {
                    // TODO: We could make the "id" prop configurable via props, using "i" might be dangerous
                    const rowId =
                        `${props.id}-${DATA_TABLE_ROW}-${item.id || i}` ||
                        shortid.generate();
                    return (
                        <DataTableRow
                            assistiveText={assistiveText}
                            canSelectRows={canSelectRows}
                            columns={columns}
                            fixedLayout={props.fixedLayout}
                            id={rowId}
                            item={item}
                            key={rowId}
                            onToggle={handleRowToggle}
                            selection={props.selection}
                            rowActions={RowActions}
                            detailComponent={props.detailsComponent}
                        />
                    );
                })
                : // Someday this should be an element to render when the table is empty
                null}
            </tbody>
        </table>
    );
}

// ### Display Name
// Always use the canonical component name as the React display name.
DataTable.displayName = DATA_TABLE;

DataTable.defaultProps = {
    assistiveText: {
        actionsHeader: 'Actions',
        columnSort: 'Sort by: ',
        columnSortedAscending: 'Sorted Ascending',
        columnSortedDescending: 'Sorted Descending',
        selectAllRows: 'Select all rows',
        selectRow: 'Select row',
    },
    id: shortid.generate(),
    selection: [],
};

// ### Prop Types
DataTable.propTypes = {
    /**
     * **Assistive text for accessibility.**
     * This object is merged with the default props object on every render.
     * * `actionsHeader`: Text for heading of actions column
     * * `columnSort`: Text for sort action on table column header
     * * `columnSortedAscending`: Text announced once a column is sorted in ascending order
     * * `columnSortedDescending`: Text announced once a column is sorted in descending order
     * * `selectAllRows`: Text for select all checkbox within the table header
     * * `selectRow`: Text for select row
     */
    assistiveText: PropTypes.shape({
        actionsHeader: PropTypes.string,
        columnSort: PropTypes.string,
        columnSortedAscending: PropTypes.string,
        columnSortedDescending: PropTypes.string,
        selectAllRows: PropTypes.string,
        selectRow: PropTypes.string,
    }),
    /**
     * Provide children of the type `<DataTableColumn />` to define the structure of the data being represented and children of the type `<DataTableRowActions />` to define a menu which will be rendered for each item in the grid. Use a _higher-order component_ to customize a data table cell that will override the default cell rendering. `CustomDataTableCell` must have the same `displayName` as `DataTableCell` or it will be ignored. If you want complete control of the HTML, including the wrapping `td`, you don't have to use `DataTableCell`.
     * ```
     * import DataTableCell from 'design-system-react/data-table/cell';
     * const CustomDataTableCell = ({ children, ...props }) => (
     *   <DataTableCell {...props} >
     *   <a href="javascript:void(0);">{children}</a>
     *   </DataTableCell>
     * );
     * CustomDataTableCell.displayName = DataTableCell.displayName;
     *
     * <DataTable>
     *   <DataTableColumn />
     *   <DataTableColumn>
     *   <DataTableCustomCell />
     *   </DataTableColumn>
     *   <DataTableRowActions />
     * </DataTable>
     * ```
     */
    children: PropTypes.node,
    /**
     * Class names to be added to the table.
     */
    className: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
    ]),
    /**
     * A variant which adds border to the vertical columns.
     */
    columnBordered: PropTypes.bool,
    /**
     * A unique ID is needed in order to support keyboard navigation and ARIA support.
     */
    id: PropTypes.string,
    /**
     * Use this if you are creating an advanced table (selectable, sortable, or resizable rows)
     */
    fixedLayout: PropTypes.bool,
    /**
     * The collection of items to render in the table. This is an array of objects with each object having keys that correspond with the  `property` prop of each `DataTableColumn`.
     * Was:
     items: PropTypes.arrayOf(
     PropTypes.shape({
            id: PropTypes.string.isRequired,
        })
     ).isRequired,
     */
    items: PropTypes.array.isRequired,

    /**
     * A variant which removes hover style on rows
     */
    noRowHover: PropTypes.bool,
    /**
     * This function fires when the selection of rows changes. This component passes in `event, { selection }` to the function. `selection` is an array of objects from the `items` prop.
     *
     * This used to be `onChange` which is deprecated now, so that the parameters can be consistent with other components. `onChange` passed in the selection first and the event wtihout a data object.
     */
    onRowChange: PropTypes.func,
    onChange: CustomPropTypes.deprecated("Use DataTable.onRowChange"),
    /**
     * This function fires when the table should be sorted.
     */
    onSort: PropTypes.func,
    /**
     * An array of objects of selected rows. See `items` prop for shape of objects.
     */
    selection: PropTypes.array,
    /**
     * True if rows should be selectable.
     */
    selectRows: PropTypes.bool,
    /**
     * A variant which modifies table layout by stacking cells to accommodate smaller viewports. Should not be used at the same time as `stackedHorizontal`.
     */
    stacked: PropTypes.bool,
    /**
     * A variant which modifies table layout by displaying the header and row data side by side for smaller viewports. Should not be used at the same time as `stacked`.
     */
    stackedHorizontal: PropTypes.bool,
    /**
     * A variant which adds stripes to alternating rows.
     */
    striped: PropTypes.bool,
    /**
     * Tables have horizontal borders by default. This removes them.
     */
    unborderedRow: PropTypes.bool,
    /**
     * A variant which removes horizontal padding. CSS class will be removed if `fixedLayout==true`.
     */
    unbufferedCell: PropTypes.bool,
    /**
     * Show details below row when selecting a row
     * TODO: Make this dependent on a callback funtion to render the details!
     */
    detailsComponent: PropTypes.func,

};

export default DataTable;
